import { connect } from 'react-redux';
import { branch, compose, lifecycle, renderNothing, withHandlers, withProps, withState } from 'recompose';

import { fetchCampaign, rejectCampaign, submitCampaign } from './actions';
import withNotifications from '../../common/withNotifications';
import { getIsDrawerOpened, isInitialLoansLoadDone } from '../../loans/selectors';
import { shouldOpenClockInModal, wasTimerFetched } from '../../main/timeTracker/selectors';
import { getCurrentUserId, getFirstLogin, getFrontendFlags, isBrandViewActive } from '../../auth/selectors';
import { Logger } from '../../../modules/infra/observability/logger';
import { FullstoryService } from '../../../modules/infra/observability/fullstory';

const NpsScalePopupContainer = compose(
  withNotifications,
  connect((state) => {
    let surveyDisplayBlockers = {};
    let canInitialize = false;
    let canShowSurvey = false;

    if (isBrandViewActive(state)) {
      // brand view doesn't have popups that would stop us from showing the NPS popup
      canInitialize = true;
      canShowSurvey = true;
    } else {
      surveyDisplayBlockers = {
        clockIn: !!shouldOpenClockInModal(state),
        firstLogin: !!getFirstLogin(state),
        loan: !!getIsDrawerOpened(state),
        screenSize: window.innerWidth < 950,
      };

      const isLoanStatusLoaded = isInitialLoansLoadDone(state);

      // start loading NPS only after we load time tracking and loan statuses to make sure they will not appear on top
      canInitialize = wasTimerFetched(state) && isLoanStatusLoaded;

      // make sure there are no other popups in the view
      canShowSurvey = Object.values(surveyDisplayBlockers).every(value => !value);
    }

    return {
      canInitialize,
      canShowSurvey,
      surveyDisplayBlockers,
      userId: getCurrentUserId(state),
      surveyRequired: getFrontendFlags(state).surveyRequired,
    };
  }),
  branch(
    props => !props.canInitialize,
    renderNothing,
  ),
  withState('campaign', 'setCampaign', null),
  withState('activeQuestionId', 'setActiveQuestionId', null),
  withState('answers', 'setAnswers', {}),
  lifecycle({
    async componentDidMount() {
      if (this.props.surveyRequired && !this.props.canShowSurvey) {
        Logger.log(
          `NPS collection popup user id ${this.props.userId} was supposed to be displayed but is blocked due to: `,
          this.props.surveyDisplayBlockers,
        );
      }

      if (this.props.surveyRequired && this.props.canShowSurvey) {
        const response = await fetchCampaign(this.props.userId);
        if (response && response.campaign) {
          setTimeout(() => {
            this.props.setCampaign(response.campaign);
            this.props.setActiveQuestionId(response.campaign.survey.questions[0].id);
          }, 3000);
        }
      }
    },
  }),
  withProps(props => ({
    didCampaignLoad: !!props.campaign && !!props.activeQuestionId,
  })),
  withHandlers({
    reset: props => () => {
      props.setCampaign(null);
      props.setActiveQuestionId(null);
      props.setAnswers({});
    },
    save: props => async (answers) => {
      const data = {
        userCampaignId: props.campaign.userCampaignId,
        userId: props.userId,
        platform: 'web',
        answers: Object.keys(answers).reduce((memo, questionId) => ([
          ...memo,
          {
            id: questionId,
            answer: answers[questionId].value,
          },
        ]), []),
      };

      await submitCampaign(data);

      props.addNotification({
        title: 'Success',
        level: 'success',
        message: 'Thanks, your feedback was submitted',
      });
    },
  }),
  branch(
    props => !props.didCampaignLoad,
    renderNothing,
  ),
  withProps((props) => {
    const questions = props.campaign.survey.questions;
    const activeQuestion = questions.find(q => q.id === props.activeQuestionId);
    return {
      activeQuestion,
      isLastQuestion: questions.indexOf(activeQuestion) === questions.length - 1,
    };
  }),
  withHandlers({
    answerQuestion: props => (answer) => {
      const answers = {
        ...props.answers,
        [props.activeQuestionId]: {
          value: answer,
        },
      };

      props.setAnswers(answers);

      if (props.isLastQuestion) {
        props.save(answers);
        props.reset();

        if (answer) {
          FullstoryService.track('Survey Answer Sent', { answerLength: answer.length });
        }
      } else {
        const questions = props.campaign.survey.questions;
        const activeQuestion = questions.find(q => q.id === props.activeQuestionId);
        const nextQuestionId = questions[questions.indexOf(activeQuestion) + 1].id;
        props.setActiveQuestionId(nextQuestionId);

        FullstoryService.track('Survey Score Sent', { score: answer });
      }
    },
    close: props => (shouldSave = false) => {
      if (shouldSave) {
        props.save(props.answers);
      } else {
        rejectCampaign(props.campaign.userCampaignId);
      }

      props.reset();
    },
  }),
  lifecycle({
    componentDidMount() {
      FullstoryService.track('Survey Shown', { campaignId: this.props.campaign.id });
    },
  }),
);

export default NpsScalePopupContainer;
